import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import {
  FormPageHeader,
  Input,
  Modal,
  ModalCloseButton,
  ToggleInput,
} from 'components/Shared';
import { MODAL_DISMISS_ACTION } from 'constants/Common';
import type { CreateAreaData } from 'contracts/Areas';
import { useValidation } from 'hooks';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import type { AppDispatch, RootState } from 'store';
import { CreateAreaActions, ListAreasActions } from 'store/ducks/areas';
import { ListBuildingsActions } from 'store/ducks/buildings';
import { CreateAreaValidator } from 'validators/Areas';
import { countBuildingsByArea, normalizeAreaCode } from './helpers';
import * as S from './styles';

export const AreasManager = () => {
  const dispatch: AppDispatch = useDispatch();
  const navigate = useNavigate();
  const formRef = useRef<FormHandles>(null);
  const { handleApiErrors, handleFormErrors } = useValidation();
  const [isOpen, setIsOpen] = useState(false);
  const { data, loading } = useSelector((state: RootState) => state.listAreas);
  const { data: buildings } = useSelector(
    (state: RootState) => state.listBuildings
  );
  const createState = useSelector((state: RootState) => state.createArea);

  const buildingsByArea = useMemo(
    () => countBuildingsByArea(buildings),
    [buildings]
  );

  const load = useCallback(() => {
    dispatch(ListAreasActions.request());
    dispatch(ListBuildingsActions.request());
  }, [dispatch]);

  const close = useCallback(() => {
    setIsOpen(false);
    formRef.current?.reset();
    formRef.current?.setErrors({});
    dispatch(CreateAreaActions.reset());
  }, [dispatch]);

  const success = useCallback(() => {
    close();
    load();
  }, [close, load]);

  const submit = async (formData: CreateAreaData) => {
    try {
      formRef.current?.setErrors({});
      const validData = await new CreateAreaValidator().schema.validate(
        {
          code: formData.code,
          name: formData.name,
          isDockManagement: String(formData.isDockManagement) === '1',
        },
        { abortEarly: false }
      );
      dispatch(CreateAreaActions.request(validData, success));
    } catch (error) {
      handleFormErrors(error, formRef);
    }
  };

  useEffect(() => load(), [load]);

  useEffect(() => {
    if (isOpen)
      formRef.current?.setData({
        code: '',
        name: '',
        isDockManagement: '1',
      });
  }, [isOpen]);

  useEffect(() => {
    handleApiErrors(createState.validationErrors, formRef);
  }, [createState.validationErrors, handleApiErrors]);

  useEffect(
    () => () => {
      dispatch(ListAreasActions.reset());
      dispatch(ListBuildingsActions.reset());
      dispatch(CreateAreaActions.reset());
    },
    [dispatch]
  );

  return (
    <S.MainPanel>
      <FormPageHeader
        title="Áreas"
        icon={<S.AreaIcon />}
        isLoading={loading}
        actions={
          <S.Button size="small" onClick={() => setIsOpen(true)}>
            <S.PlusIcon /> Nova área
          </S.Button>
        }
      />
      {!data.length ? (
        <S.EmptyListPlaceholder>Nenhuma área cadastrada</S.EmptyListPlaceholder>
      ) : (
        <S.List>
          <S.ListHeader>
            <div>ID</div>
            <div>NOME</div>
            <div>CÓDIGO</div>
            <div>PRÉDIOS</div>
            <div />
          </S.ListHeader>
          {data.map((area) => (
            <S.ListItem key={area.id}>
              <S.Column>{area.id}</S.Column>
              <S.Column>{area.name}</S.Column>
              <S.Column>{area.code}</S.Column>
              <S.Column>{buildingsByArea[area.id] ?? 0}</S.Column>
              <S.ActionsColumn>
                <S.ActionButton
                  aria-label={`Editar área ${area.name}`}
                  onClick={() => navigate(`/configuracoes/areas/${area.id}`)}
                >
                  <S.EditIcon />
                </S.ActionButton>
              </S.ActionsColumn>
            </S.ListItem>
          ))}
        </S.List>
      )}
      <Modal isOpen={isOpen} onClickOutside={close}>
        <S.ModalContent style={{ maxWidth: '540px' }}>
          <S.ModalHeader>
            <h4>Nova área</h4>
            <ModalCloseButton onClick={close} />
          </S.ModalHeader>
          <S.ModalBody>
            <Form ref={formRef} onSubmit={submit}>
              <Input
                name="name"
                label="Nome"
                onChange={(event) =>
                  formRef.current?.setFieldValue(
                    'code',
                    normalizeAreaCode(event.target.value)
                  )
                }
              />
              <Input name="code" label="Código" />
              <ToggleInput
                name="isDockManagement"
                label="Disponível para gestão de docas/armazém"
              />
              <S.FormActions>
                <S.Button type="button" mood="light" onClick={close}>
                  {MODAL_DISMISS_ACTION}
                </S.Button>
                <S.Button type="submit">
                  {createState.loading ? <S.ActivityIndicator /> : 'Salvar'}
                </S.Button>
              </S.FormActions>
            </Form>
          </S.ModalBody>
        </S.ModalContent>
      </Modal>
    </S.MainPanel>
  );
};
